import { faFileSpreadsheet } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Flex,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  Text,
  TextButton,
} from '@modusign/design-system';
import { startBatchManagementOfDocuments } from '@modusign/openapi-client-external-document';
import { AxiosError } from 'axios';
import { useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';

import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';

import uploadFile from '../../../libs/uploadFile';
import { batchUpdateStarted } from '../../../state';
import BatchUpdateErrorModal from '../../BatchUpdateErrorModal';

interface UploadBatchTemplateModalProps {
  isOpen: boolean;
  file: File;
  setFile: (file: File) => void;
  onClose: () => void;
}

export default function UploadBatchTemplateModal({
  isOpen,
  file,
  setFile,
  onClose,
}: UploadBatchTemplateModalProps) {
  const dispatch = useDispatch();
  const workspaceId = useWorkspaceId();
  const { handleError } = useOpenErrorModal();
  const [validationErrors, setValidationErrors] = useState<Array<{
    type: string;
    cell: string;
    error: string;
  }> | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: startUploadFile, isLoading } = useMutation(
    async () => {
      const key = await uploadFile(file, 'BATCH_EXCEL');
      const result = await startBatchManagementOfDocuments(workspaceId, { key });
      dispatch(batchUpdateStarted(result));
    },
    {
      onError: (error: AxiosError) => {
        if (error.response?.status === 400) {
          setValidationErrors(error.response.data.data.errors);
        } else {
          handleError({ title: '엑셀 업로드 실패', error });
        }
      },
      onSuccess: onClose,
    }
  );

  if (validationErrors) {
    return (
      <BatchUpdateErrorModal
        errors={validationErrors}
        isOpen
        onClose={onClose}
        onSetFile={(file) => {
          setFile(file);
          setValidationErrors(null);
        }}
      />
    );
  }

  return (
    <Modal isOpen={isOpen} maxWidth="sm" onClose={onClose}>
      <input
        accept=".xlsx"
        hidden
        ref={fileInputRef}
        type="file"
        onChange={(e) => {
          if (e.target.files?.[0]) {
            setFile(e.target.files?.[0]);
          }
        }}
      />
      <ModalTitle>엑셀 업로드</ModalTitle>
      <ModalContent>
        <Text textAlign="center">파일 선택이 완료되었습니다.</Text>
        <Flex
          alignItems="center"
          bg="boxBackgroundNeutralBase"
          border="1px solid"
          borderColor="boxBorderNeutralBase"
          mt={7}
          p={6}
        >
          <FontAwesomeIcon icon={faFileSpreadsheet} style={{ flex: 'none' }} />
          <Text
            fontWeight="medium"
            sx={{
              ml: 2,
              flex: 'auto',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {file?.name}
          </Text>
          <TextButton
            disabled={isLoading}
            sx={{ flex: 'none' }}
            onClick={() => fileInputRef.current?.click()}
          >
            재업로드
          </TextButton>
        </Flex>
      </ModalContent>
      <ModalFooter>
        <Button disabled={isLoading} onClick={onClose}>
          취소
        </Button>
        <Button isLoading={isLoading} ml={3} variant="primary" onClick={() => startUploadFile()}>
          업로드하기
        </Button>
      </ModalFooter>
    </Modal>
  );
}
